import { useTheme } from "../../../contexts/ThemeContext";

const stats = [
  { value: "< 30s", label: "Response time", detail: "vs. 47-minute industry average", color: "#F97316" },
  { value: "3×", label: "Booking rate", detail: "compared to manual follow-up", color: "#22C55E" },
  { value: "98%", label: "Lead capture rate", detail: "zero missed opportunities, 24/7", color: "#38BDF8" },
  { value: "$0", label: "Added payroll", detail: "infinite scale, no new staff", color: "#F97316" },
];

const advantages = [
  { icon: "ri-flashlight-line", color: "#F97316", title: "Instant Response", desc: "Every lead hears from you in under 30 seconds. The business that responds first wins — every time." },
  { icon: "ri-shield-check-line", color: "#22C55E", title: "Zero Human Error", desc: "No fatigue, no bad days, no missed messages. Every interaction is consistent, professional, and on-brand." },
  { icon: "ri-bar-chart-grouped-line", color: "#38BDF8", title: "Predictable Revenue", desc: "Automate the top of your funnel and transform unpredictable bookings into a reliable, scalable pipeline." },
  { icon: "ri-focus-3-line", color: "#F97316", title: "Owner Freedom", desc: "Stop managing your front desk and start managing your business. AI handles inbound — you focus on delivery." },
  { icon: "ri-money-dollar-circle-line", color: "#22C55E", title: "More Revenue", desc: "Every recovered missed call and automated follow-up converts directly to revenue — without adding headcount." },
  { icon: "ri-plug-line", color: "#38BDF8", title: "Seamless Setup", desc: "No IT department needed. The system integrates with your existing tools and is live in days, not months." },
];

export default function Benefits() {
  const { isDark } = useTheme();

  const sectionBg = isDark ? "bg-[#050508]" : "bg-[#F5F5F7]";
  const titleColor = isDark ? "text-white" : "text-[#1d1d1f]";
  const subColor = isDark ? "text-white/45" : "text-[#6e6e73]";
  const cardBg = isDark ? "bg-[#0c0c14] border-white/[0.06] hover:border-white/14" : "bg-white border-black/[0.07] hover:border-black/14";
  const advantageTitle = isDark ? "text-white" : "text-[#1d1d1f]";
  const advantageDesc = isDark ? "text-white/45" : "text-[#6e6e73]";
  const compareBorderColor = isDark ? "border-white/[0.06]" : "border-black/[0.07]";
  const withoutBg = isDark ? "bg-[#100808]" : "bg-[#FFF5F5]";
  const withBg = isDark ? "bg-[#070F08]" : "bg-[#F0FAF0]";
  const withoutText = isDark ? "text-white/40" : "text-black/45";
  const withText = isDark ? "text-white/70" : "text-[#1d1d1f]/75";

  return (
    <section id="benefits" className={`${sectionBg} py-24 md:py-36 px-6 md:px-10 transition-colors duration-300`}>
      <div className="max-w-[1280px] mx-auto">

        <div className="text-center mb-20">
          <p className="text-[#22C55E] text-[11px] font-semibold tracking-[0.2em] uppercase mb-5">The Impact</p>
          <h2 className={`text-[42px] md:text-[60px] font-black ${titleColor} tracking-[-0.03em] leading-tight mb-5`}>
            Results that show up<br />on day one.
          </h2>
          <p className={`${subColor} text-[17px] leading-relaxed max-w-lg mx-auto`}>
            No lengthy onboarding. No waiting period. AI Talent Launch produces measurable, trackable growth from the moment it goes live.
          </p>
        </div>

        {/* Stats banner — always cinematic */}
        <div className="relative rounded-[28px] overflow-hidden mb-16">
          <img
            src="https://public.readdy.ai/ai/img_res/61282b84feb2f1bb88b0b080a9cb84d9.jpg"
            alt="Growth"
            className="w-full h-[480px] md:h-[540px] object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 w-full max-w-4xl text-center">
              {stats.map((s, i) => (
                <div key={i}>
                  <div className="text-[52px] md:text-[68px] font-black leading-none tracking-tight mb-2" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-white text-[14px] font-semibold mb-1">{s.label}</div>
                  <div className="text-white/40 text-[12px]">{s.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Advantages */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {advantages.map((item, i) => (
            <div key={i} className={`group border rounded-[20px] p-7 transition-all duration-300 ${cardBg}`}>
              <div className="w-10 h-10 flex items-center justify-center mb-5">
                <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
              </div>
              <h4 className={`${advantageTitle} font-bold text-[16px] mb-2`}>{item.title}</h4>
              <p className={`${advantageDesc} text-[14px] leading-relaxed`}>{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Comparison */}
        <div className={`mt-10 rounded-[24px] border ${compareBorderColor} overflow-hidden grid md:grid-cols-2`}>
          <div className={`${withoutBg} p-8 md:p-10 border-b md:border-b-0 md:border-r ${compareBorderColor}`}>
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase mb-6" style={{ color: "#F97316" }}>Without AI Talent Launch</p>
            <ul className="flex flex-col gap-3.5">
              {["Missed calls go to voicemail", "Leads sit idle for hours", "Follow-ups rely on staff memory", "Revenue lost to faster competitors", "Unpredictable, inconsistent bookings"].map((item, i) => (
                <li key={i} className={`flex items-center gap-3 ${withoutText} text-[14px]`}>
                  <i className="ri-close-circle-line text-[15px] shrink-0" style={{ color: "#F9731650" }}></i>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className={`${withBg} p-8 md:p-10`}>
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase mb-6" style={{ color: "#22C55E" }}>With AI Talent Launch</p>
            <ul className="flex flex-col gap-3.5">
              {["Every call answered in under 30 seconds", "Instant lead response, around the clock", "Automated follow-ups — zero manual effort", "First to respond wins — every time", "Consistent, predictable booking pipeline"].map((item, i) => (
                <li key={i} className={`flex items-center gap-3 ${withText} text-[14px]`}>
                  <i className="ri-checkbox-circle-line text-[15px] shrink-0" style={{ color: "#22C55E80" }}></i>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
